import React from 'react';
import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import { isMobile, isIOS } from 'react-device-detect';
import { connect } from 'react-redux';
import { Button } from '@picsio/ui';
import Icon from '../Icon';

import Logger from '../../services/Logger';
import localization from '../../shared/strings';
import picsioConfig from '../../../../../config';

import DownloadDialogPresets from './DownloadDialogPresets';
import DownloadDialogSettings from './DownloadDialogSettings';
import DownloadDialogAdditional from './DownloadDialogAdditional';
import withErrorBoundary from '../ErrorBoundary/withErrorBoundary';
import * as actions from './actions';
import { showDialog } from '../dialog';
import { setDownloadPresets } from '../../store/reducers/user';

import { defaultPresets, customPreset, getOptionDefaultValue } from './config';

const { isMainApp, isProofing } = picsioConfig;

const setArchiveAndWatermarkSettings = (preset, isArchive = true, withoutWatermark = false) => ({
  ...preset,
  data: {
    ...preset.data,
    isArchive,
    withoutWatermark,
  },
});

class DownloadDialog extends React.Component {
  refBtnOk = React.createRef();

  state = {
    isLoading: false,
    hasCustomVideoPresets: false,
    hasPdfProxyPresets: false,
    isAssetsWithWatermark: false,
    presets: defaultPresets,
    selectedPreset: defaultPresets[0],
  };

  componentDidMount() {
    const {
      shouldFormatBeDisabled,
      countAssets,
      assets,
      subscriptionFeatures,
      downloadPresets,
    } = this.props;
    const isAssetsWithWatermark = assets.some((asset) => asset.watermarkId);
    const displayWatermark = subscriptionFeatures.watermarks || !isMainApp;

    if (isAssetsWithWatermark && displayWatermark) {
      this.setState({ isAssetsWithWatermark });
    }

    // download more than 1 asset as archive on mobile iOS devices
    if (countAssets > 1 && isMobile && isIOS) {
      this.setState(({ selectedPreset, presets }) => ({
        selectedPreset: setArchiveAndWatermarkSettings(selectedPreset),
        presets: presets.map((preset) => setArchiveAndWatermarkSettings(preset)),
      }));
    }

    if (isMainApp && !shouldFormatBeDisabled) {
      this.setPresets(this.getPreparedPresets(downloadPresets));
    }

    if (assets.every((asset) => !!asset.pdfProxies)) {
      const pdfProxyPreset = {
        _id: 'downloadPdf',
        name: 'Download as PDF',
        data: {
          usePdfProxy: true,
          mimeType: 'original',
          isArchive: false,
          withoutWatermark: false,
          organizeByCollections: false,
        },
      };

      this.setState(({ presets }) => ({
        presets: [...presets, pdfProxyPreset],
        hasPdfProxyPresets: true,
      }));
    }

    /** add download qualities to presets for videos with proxy versions only */
    if (isMainApp && assets.every((a) => !!a.customVideo)) {
      const assetsQualities = assets.map((a) => {
        if (Array.isArray(a.customVideo.head)) {
          return a.customVideo.head.map((q) => q.quality);
        }
        return null;
      });
      if (!assetsQualities.includes(null)) {
        const diff = assetsQualities.reduce((acc, q, index) => {
          if (index === 0) return q;
          return acc.filter((i) => q.indexOf(i) > -1);
        }, []);
        if (diff.length > 0) {
          const videoPresets = diff.map((resolution) => ({
            _id: resolution.toString(),
            name: `${resolution}p`,
            data: {
              resolution,
              mimeType: 'original',
              isArchive: false,
              withoutWatermark: false,
              organizeByCollections: false,
            },
          }));
          this.setState(({ presets }) => ({
            presets: [...presets, ...videoPresets],
            hasCustomVideoPresets: true,
          }));
        } else {
          Logger.info('Download dialog: videos has no the same qualities');
        }
      } else {
        Logger.info('Download dialog: some video includes custom video without qualities');
      }
    }
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  setLoading = (isLoading) => this.setState({ isLoading });

  setPresets = (presets) => this.setState({ presets });

  setSelectedPreset = (preset) => this.setState({ selectedPreset: preset });

  setPreset = (preset) => this.setState(({ presets }) => {
    const isExist = presets.find(({ _id }) => _id === preset._id);
    const nextPresets = !isExist
      ? [...presets, preset]
      : presets.map((item) => {
        const { _id } = item;
        if (_id === preset._id) {
          return preset;
        }
        return item;
      });

    return { presets: nextPresets };
  });

  setPresetValue = (name, value) => this.setState(({ selectedPreset }) => ({
    selectedPreset: {
      ...selectedPreset,
      data: {
        ...selectedPreset.data,
        [name]: value,
      },
    },
  }));

  getPreparedPresets = (downloadPresets = []) => {
    let data = [...defaultPresets, customPreset, ...downloadPresets];

    // download more than 1 asset as archive on mobile iOS devices
    if (this.props.countAssets > 1 && isMobile && isIOS) {
      data = data.map((preset) => setArchiveAndWatermarkSettings(preset));
    }

    return data;
  };

  removePreset = (presetId) => {
    const { presets, selectedPreset } = this.state;
    const isSelected = selectedPreset._id === presetId;
    const presetIndex = presets.findIndex(({ _id }) => _id === presetId);
    const prevPreset = presets[presetIndex - 1];

    this.setState({
      selectedPreset: isSelected ? prevPreset : selectedPreset,
      presets: presets.filter(({ _id }) => _id !== presetId),
    });
  };

  handleChangePreset = (e) => {
    const {
      target: { value },
    } = e;
    const { presets } = this.state;
    const selectedPreset = presets.find(({ _id }) => _id === value);

    this.setSelectedPreset(selectedPreset);
  };

  handleChangeSettings = (e) => {
    const {
      target: {
        type, name, value, checked,
      },
    } = e;
    let validValue = value;

    switch (type) {
    case 'number':
      validValue = value;
      break;
    case 'checkbox':
      validValue = checked;

      if (name === 'isArchive' && !checked) {
        this.setPresetValue('organizeByCollections', checked);
      }
      if (name === 'withoutWatermark' && !checked) {
        this.setPresetValue('organizeByCollections', checked);
      }
      break;
    default: {
      break;
    }
    }
    this.setPresetValue(name, validValue);
  };

  handleBlurSettings = (e) => {
    const {
      target: {
        type, name, value,

      },
    } = e;

    if (type === 'number' && !Number(value)) {
      this.setPresetValue(name, 1);
    }
  }

  saveDownloadPreset = async (name, shared, isNew) => {
    const { selectedPreset } = this.state;
    const { _id, data } = selectedPreset;

    try {
      this.setLoading(true);

      const requestData = {
        name,
        data,
        shared,
      };
      let preset;

      if (isNew) {
        preset = await actions.createDownloadPreset(requestData);
      } else {
        preset = await actions.updateDownloadPreset({ _id, ...requestData });
      }

      const nextDownloadPresets = this.props.downloadPresets.some(
        ({ _id: itemId }) => itemId === preset._id,
      )
        ? this.props.downloadPresets.map((item) => (item._id === preset._id ? preset : item))
        : [...this.props.downloadPresets, preset];

      this.props.setDownloadPresets(nextDownloadPresets);
      this.setPreset(preset);
      this.setSelectedPreset(preset);
    } catch (e) {
      showDialog({
        title: localization.ERROR,
        text: localization.DOWNLOADDIALOG.saveDownloadPresetError,
      });
    } finally {
      this.setLoading(false);
    }
  };

  handleSavePreset = async () => {
    const { selectedPreset } = this.state;
    const { _id, name, teamId: isShared } = selectedPreset;
    const isNew = _id === 'custom';
    const presetName = isNew ? '' : name;
    const title = isNew
      ? localization.DOWNLOADDIALOG.createDownloadPreset
      : localization.DOWNLOADDIALOG.saveDownloadPreset;

    showDialog({
      title,
      input: {
        label: 'Name',
        type: 'text',
        value: presetName,
      },
      checkbox: {
        value: !!isShared,
        label: 'Share with my team',
      },
      disableOk: ({ input }) => !input,
      onOk: ({ input: newName, checkbox: shared }) => {
        this.saveDownloadPreset(newName, shared, isNew);
      },
    });
  };

  removeDownloadPreset = async (presetId) => {
    try {
      this.setLoading(true);

      await actions.deleteDownloadPreset(presetId);

      this.props.setDownloadPresets(
        this.props.downloadPresets.filter(({ _id }) => _id !== presetId),
      );
      this.removePreset(presetId);
    } catch (e) {
      showDialog({
        title: localization.ERROR,
        text: localization.DOWNLOADDIALOG.removeDownloadPresetError,
      });
    } finally {
      this.setLoading(false);
    }
  };

  handleRemovePreset = async (presetId, presetName) => {
    showDialog({
      title: localization.DOWNLOADDIALOG.removeDownloadPreset,
      text: localization.DOWNLOADDIALOG.removeDownloadPresetText(presetName),
      onOk: () => {
        this.removeDownloadPreset(presetId);
      },
    });
  };

  handleKeyDown = (e) => {
    switch (e.keyCode) {
    // if press Esc
    case 27:
      Logger.log('User', 'DownloadDialogCancel');
      this.props.close();
      break;
      // if press Enter
    case 13:
      this.handleSubmit();
      break;
    default:
      break;
    }
  };

  handleSubmit = () => {
    const { selectedPreset, isAssetsWithWatermark } = this.state;
    const isWatermarkingAllowed = this.props.subscriptionFeatures.watermarks;
    const { _id, data } = selectedPreset;
    const {
      mimeType: rawMimeType,
      quality,
      density,
      units,
      resizing,
      width,
      isArchive,
      organizeByCollections,
      withoutWatermark,
      resolution,
      usePdfProxy,
    } = data;
    // `customPreset` uses `mimeType: 'custom'` as a UI marker for the AssetsCopyDialog,
    // but the zipper doesn't know this value and would build URLs with `.undefined`
    // extensions. In the Download dialog the Custom preset means "keep original format",
    // so we normalize it to 'original' before submitting.
    const mimeType = rawMimeType === 'custom' ? 'original' : rawMimeType;
    const isOriginalPreset = _id === 'original';
    const isDefaultFormat = mimeType === 'original';
    const isOriginal = isMainApp ? isOriginalPreset && isDefaultFormat : isDefaultFormat;
    const displayWatermark = isWatermarkingAllowed || !isMainApp;

    const config = {
      mimeType,
      organizeByCollections,
      isArchive,
      withoutWatermark: displayWatermark ? withoutWatermark : true,
      resolution,
      usePdfProxy,
    };

    if (!isOriginal) {
      config.quality = quality;
      config.resizing = resizing;
      config.density = null;

      if (density !== getOptionDefaultValue('density')) {
        config.density = density;
        config.units = units;
      }
      if (resizing !== getOptionDefaultValue('resizing')) {
        config.width = Number(width);
      }
    }
    if (isAssetsWithWatermark) {
      config.ignoreCache = true;
    }

    this.props.onDownload(config);
    if ((isMainApp || isProofing) && !isMobile) {
      setTimeout(this.runFlyingFiles, 100); // fix for flying download icon
    }
  };

  runFlyingFiles = () => {
    if (!this.refBtnOk || (this.refBtnOk && this.refBtnOk.current === null)) return;
    const { offsetLeft, offsetTop } = this.refBtnOk.current;

    const el = document.createElement('div');
    el.classList.add('flyingFiles');
    const root = createRoot(el);
    root.render(<Icon name="files" />);
    el.style.left = `${offsetLeft}px`;
    el.style.top = `${offsetTop}px`;
    el.addEventListener('transitionend', el.remove, false);

    document.body.appendChild(el);

    const ElDestination = document.querySelector('.toolbarPreviewLeft .downloadList')
      || document.querySelector('.toolbarCatalogLeft .downloadList')
      || document.querySelector('.toolbarCatalogLeft .toolbarGroup .toolbarButton:last-child')
      || document.querySelector('.toolbarCatalogTop .downloadList')
      || document.querySelector('.toolbarSelectedAssetsTop .toolbarDownload')
      || document.querySelector('.toolbarCatalogProofing .toolbarCatalogTop #button-downloadList');

    if (ElDestination) {
      const {
        top, left, height, width,
      } = ElDestination.getBoundingClientRect();

      el.style.left = `${left + width * 0.5}px`; // increase to centrify position
      el.style.top = `${top + height}px`;
    }
  };

  render() {
    const {
      isLoading,
      presets,
      selectedPreset,
      hasCustomVideoPresets,
      hasPdfProxyPresets,
      isAssetsWithWatermark,
    } = this.state;
    const {
      close, shouldFormatBeDisabled, countAssets, assets, subscriptionFeatures,
    } = this.props;
    const isWatermarkingAllowed = subscriptionFeatures.watermarks;
    const displayWatermark = isWatermarkingAllowed || !isMainApp;
    const isOpenSettings = !isMainApp
      || (
        selectedPreset._id !== 'original'
        && !hasCustomVideoPresets
        && !hasPdfProxyPresets
      );
    const canDownloadWithoutWatermark = isMainApp
      && isAssetsWithWatermark
      && assets[0].permissions.downloadWithoutWatermark
      && displayWatermark;

    const disablePdfFormat = isAssetsWithWatermark
      && selectedPreset.data.withoutWatermark === false;

    return (
      <div className="simpleDialog downloadDialog">
        <div className="simpleDialogUnderlayer" />
        <div className="simpleDialogBox">
          <div className="simpleDialogHeader">
            <h2 className="simpleDialogTitle">{localization.DOWNLOADDIALOG.titleDownload}</h2>
            <button
              className="simpleDialogBtnCross"
              onClick={() => {
                Logger.log('User', 'DownloadDialogCancel');
                close();
              }}
              type="button"
            >
              <Icon name="close" />
            </button>
          </div>
          <div className="simpleDialogContent">
            <div className="simpleDialogContentInner">
              <If condition={isMainApp || hasPdfProxyPresets}>
                <DownloadDialogPresets
                  isLoading={isLoading}
                  selectedPreset={selectedPreset}
                  presets={presets}
                  onChange={this.handleChangePreset}
                  onRemove={this.handleRemovePreset}
                  onSave={this.handleSavePreset}
                />
              </If>
              <If condition={isOpenSettings}>
                <DownloadDialogSettings
                  selectedSettings={selectedPreset.data}
                  onChange={this.handleChangeSettings}
                  isDisabledFormat={shouldFormatBeDisabled}
                  disablePdfFormat={disablePdfFormat}
                  onBlur={this.handleBlurSettings}
                />
              </If>
              <DownloadDialogAdditional
                canDownloadWithoutWatermark={canDownloadWithoutWatermark}
                selectedSettings={selectedPreset.data}
                onChange={this.handleChangeSettings}
                countAssets={countAssets}
              />
            </div>
          </div>
          <div className="simpleDialogFooter">
            <Button
              color="secondary"
              size="md"
              variant="contained"
              onClick={() => {
                Logger.log('User', 'DownloadDialogCancel');
                close();
              }}
            >
              {localization.DIALOGS.btnCancel}
            </Button>
            <Button
              color="primary"
              size="md"
              variant="contained"
              ref={this.refBtnOk}
              onClick={this.handleSubmit}
            >
              {localization.DOWNLOADDIALOG.textDownload}
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

DownloadDialog.defaultProps = {
  countAssets: 0,
  shouldFormatBeDisabled: false,
  subscriptionFeatures: {},
  downloadPresets: [],
};
DownloadDialog.propTypes = {
  countAssets: PropTypes.number,
  assets: PropTypes.array.isRequired,
  close: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  shouldFormatBeDisabled: PropTypes.bool,
  subscriptionFeatures: PropTypes.objectOf(PropTypes.any),
  downloadPresets: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
  })),
  setDownloadPresets: PropTypes.func.isRequired,
};

const ConnectedDownloadDialog = connect(
  (state) => ({
    subscriptionFeatures: state.user.subscriptionFeatures,
    downloadPresets: Array.isArray(state.user.presets) ? state.user.presets : [],
  }),
  { setDownloadPresets },
)(DownloadDialog);

export default withErrorBoundary(ConnectedDownloadDialog, { className: 'errorBoundaryOverlay' });
